import React from 'react';
import { Form, Select, Alert, Typography, Divider } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';

const { Option } = Select;
const { Text } = Typography;

/**
 * Credential Set Selector component for sources requiring CA credentials
 *
 * This component allows users to select appropriate credential sets for sources
 * that require credentials stored on the Migration Appliance.
 *
 * @param {Array} selectedSources - Sources that require CA credentials
 * @param {Object} selectedAccount - Selected cloud account with appliance
 * @param {Object} credentialSets - Current credential set selections
 * @param {Function} onCredentialSetChange - Handler for credential set changes
 */
export const CredentialSetSelector = ({ selectedSources, selectedAccount, credentialSets, onCredentialSetChange }) => {
    const availableCAcreds = selectedAccount?.appliance?.credentials || [];
    const noCACreds = !availableCAcreds || availableCAcreds.length === 0;

    return (
        <div className="credential-set-container">
            <Divider orientation="left">
                <Text strong>
                    Choose Source Credential Set{' '}
                    <QuestionCircleOutlined title="For each source, indicate the appropriate credentials set that was stored on the Migration Appliance deployed into your destination environment." />
                </Text>
            </Divider>

            {noCACreds ? (
                <Alert
                    title="No Credential Sets Available"
                    description={
                        <div>
                            No credential sets are currently stored on the migration appliance of the selected target.
                            Please choose from the following options:
                            <ol style={{ marginTop: 8, marginBottom: 0 }}>
                                <li>Select another target cloud.</li>
                                <li>
                                    Add credential set(s) to the migration appliance (see{' '}
                                    <a
                                        href="http://docs.rivermeadow.com/store-source-credentials-on-cloud-appliance"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        "Store Source Credentials on Migration Appliance" in documentation
                                    </a>
                                    ).
                                </li>
                            </ol>
                        </div>
                    }
                    type="error"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
            ) : (
                <div className="credential-sources-list">
                    {selectedSources.map((source) => (
                        <Form.Item
                            key={source.id}
                            label={`${source.hostname || source.host} Credentials`}
                            name={`credentialSet_${source.id}`}
                            rules={[{ required: true, message: 'Please select a credential set' }]}
                            style={{ marginBottom: 12 }}
                        >
                            <Select
                                placeholder="Select Credential Set"
                                value={credentialSets[source.id]}
                                onChange={(value) => onCredentialSetChange(source.id, value)}
                                className="source-ca-credentials"
                                data-source-id={source.id}
                            >
                                <Option value="">Select Credential Set</Option>
                                {availableCAcreds.map((cred, index) => (
                                    <Option key={cred.tag || cred || index} value={cred.tag || cred}>
                                        {cred.tag || cred}
                                        {cred.description && ` (${cred.description})`}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    ))}
                </div>
            )}
        </div>
    );
};
